import React, {
  ReactElement, useCallback, useEffect, useState,
} from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import Swal from 'sweetalert2';
import { isEqual } from 'lodash';
import * as Yup from 'yup';
import { GoogleReCaptchaProvider, useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { FORM_SUBMISSIONS_TESTIMONIALS_BROWSER_API_PREFIX, GOOGLE_RECAPTCHA_KEY } from '../../utils/constants';
import { Media } from '../../structures/models/media/media';
import { testimonialValidator } from './validators/form-validators';
import TestimonialForm from './TestimonialForm';

interface Props {
  testimonial: {
    name: string,
    email: string,
    body: string,
    breeder_phone: string,
    breeder_name: string,
    title: string,
    slug: string,
    images: Array<Media>,
    status: string,
    id?: number,
  },
}

const TestimonialEdit: React.FC<Props> = ({
  testimonial,
}): ReactElement => {
  const [formValues, setFormValues] = useState<{
    name: string,
    email: string,
    body: string,
    breeder_phone: string,
    breeder_name: string,
    title: string,
    slug: string,
    images: Array<Media>,
    status: string,
    id?: number,
  }>({
    name: testimonial.name,
    email: testimonial.email,
    title: testimonial.title,
    slug: testimonial.slug,
    body: testimonial.body,
    breeder_name: testimonial.breeder_name,
    breeder_phone: testimonial.breeder_phone,
    status: testimonial.status,
    images: testimonial.images,
  });
  const [loadingError, setLoadingError] = useState('');
  const [loading, setLoading] = useState(false);
  const [cloudinaryIsUpdating, setCloudinaryIsUpdating] = useState(false);

  const { executeRecaptcha } = useGoogleReCaptcha();
  // we wrap executeRecaptcha because it might take a bit to initialize,
  // so we handle that possibility gracefully here
  const handleReCaptchaVerify = async () => {
    if (!executeRecaptcha) {
      return '';
    }

    return executeRecaptcha('submit');
  };

  const testimonialReviewUpdateValidator = testimonialValidator
    .shape({
      status: Yup.string().required().oneOf([
        'pending',
        'accepted',
        'rejected',
      ]),
    });

  useEffect(() => {
    if (!testimonial || !testimonial.id) {
      if (!loadingError) {
        setLoadingError('Could not load testimonial form; check your connection and try again.');
      }
    } else if (loadingError) {
      setLoadingError('');
    }
  }, [testimonial, loadingError]);

  const updateFormState = useCallback((newValues: object) => {
    const newFormState = {
      ...formValues,
      ...newValues,
    };
    if (!isEqual(newFormState, formValues)) {
      setFormValues(newFormState);
    }
  }, [formValues]);

  const onSubmit = useCallback(async (values) => {
    updateFormState(values);
    const valuesWithRecaptcha = {
      recaptcha: await handleReCaptchaVerify(),
      ...values,
    };
    setLoading(true);
    axios.patch(
      `${FORM_SUBMISSIONS_TESTIMONIALS_BROWSER_API_PREFIX}/${testimonial.id}`,
      valuesWithRecaptcha,
      { headers: { Accept: 'application/json' } },
    )
      .then((response) => {
        if (response.data?.testimonial || (response.status >= 200 && response.status <= 299)) {
          Swal.fire(
            'Success',
            'Testimonial updated successfully!',
            'success',
          ).then(() => {
            setLoading(false);
          });
        }
      })
      .catch((e) => {
        setLoading(false);
        Swal.fire(
          'Error',
          e?.message || 'Something went wrong; check your address and connection, then try again.',
          'error',
        );
      });
  }, [formValues, executeRecaptcha]);

  return (
    <div className="bg-white">
      {loadingError
        ? (
          <div className="alert alert-danger">
            <p>{loadingError}</p>
          </div>
        ) : (
          <TestimonialForm
            formValues={formValues}
            validator={testimonialReviewUpdateValidator}
            onSubmit={onSubmit}
            loading={loading}
            canUpdateStatus={true}
            cloudinaryIsUpdating={cloudinaryIsUpdating}
            setCloudinaryIsUpdating={setCloudinaryIsUpdating}
            isEditForm
          />
        )}
    </div>
  );
};

export default TestimonialEdit;

if (document.getElementById('react-testimonial-edit-form')) {
  const element = document.getElementById('react-testimonial-edit-form');
  const testimonial = element?.dataset.testimonial
    ? JSON.parse(element.dataset.testimonial)
    : undefined;
  ReactDOM.render(
    <GoogleReCaptchaProvider reCaptchaKey={GOOGLE_RECAPTCHA_KEY || ''}>
      <TestimonialEdit
        testimonial={testimonial}
      />
    </GoogleReCaptchaProvider>,
    document.getElementById('react-testimonial-edit-form'),
  );
}
